import { useCallback, useEffect, useRef, useState } from 'react';

import { useMediaDeviceSelect, useMediaDevices, useRoomContext } from '@livekit/components-react';
import { useLocalStorage } from '@uidotdev/usehooks';
import { Mic, Video, Volume2 } from 'lucide-react';

import { logError } from '../../lib/logger';

import { buildLocalStorageKey, useConference } from '../../contexts/ConferenceContext';

// Owns device enumeration, selection, and the per-profile persisted preference for all
// three device kinds. Call this ONCE per call — the mount effect re-applies stored
// preferences, and a second instance would race it with duplicate setActiveMediaDevice
// calls. The camera and microphone menus are dumb renderers fed from here.
export function useMediaDevicePreferences() {
  const room = useRoomContext();
  const { profileId } = useConference();

  const [audioInputMediaDevice, setAudioInputMediaDevice] = useLocalStorage(
    buildLocalStorageKey(profileId, 'media-device:audio-input'),
    null,
  );
  const [videoInputMediaDevice, setVideoInputMediaDevice] = useLocalStorage(
    buildLocalStorageKey(profileId, 'media-device:video-input'),
    null,
  );
  const [audioOutputMediaDevice, setAudioOutputMediaDevice] = useLocalStorage(
    buildLocalStorageKey(profileId, 'media-device:audio-output'),
    null,
  );

  // get device lists without affecting tracks
  const audioInputDevices = useMediaDevices({ kind: 'audioinput' });
  const videoInputDevices = useMediaDevices({ kind: 'videoinput' });
  const audioOutputDevices = useMediaDevices({ kind: 'audiooutput' });

  // get current active device IDs from hooks - don't pass tracks to avoid side effects on mount
  // hooks will only act when setActiveMediaDevice is called, not on initialization
  const audioInputSelect = useMediaDeviceSelect({ kind: 'audioinput', room, requestPermissions: false });
  const videoInputSelect = useMediaDeviceSelect({ kind: 'videoinput', room, requestPermissions: false });
  const audioOutputSelect = useMediaDeviceSelect({ kind: 'audiooutput', room, requestPermissions: false });

  // handle device changes - only act when user actually changes selection
  const handleAudioInputChange = useCallback(
    async (deviceId) => {
      if (!deviceId || deviceId === audioInputSelect.activeDeviceId || !room?.localParticipant) return;
      try {
        // use setActiveMediaDevice - this should switch the device on the existing track
        await audioInputSelect.setActiveMediaDevice(deviceId);
        setAudioInputMediaDevice(deviceId);
      } catch (error) {
        logError('useMediaDevicePreferences', 'Failed to set audio input device', { error });
      }
    },
    [room, audioInputSelect, setAudioInputMediaDevice],
  );

  const handleVideoInputChange = useCallback(
    async (deviceId) => {
      if (!deviceId || deviceId === videoInputSelect.activeDeviceId || !room?.localParticipant) return;
      try {
        await videoInputSelect.setActiveMediaDevice(deviceId);
        setVideoInputMediaDevice(deviceId);
      } catch (error) {
        logError('useMediaDevicePreferences', 'Failed to set video input device', { error });
      }
    },
    [room, videoInputSelect, setVideoInputMediaDevice],
  );

  const handleAudioOutputChange = useCallback(
    async (deviceId) => {
      if (!deviceId || deviceId === audioOutputSelect.activeDeviceId) return;
      try {
        await audioOutputSelect.setActiveMediaDevice(deviceId);
        setAudioOutputMediaDevice(deviceId);
      } catch (error) {
        logError('useMediaDevicePreferences', 'Failed to set audio output device', { error });
      }
    },
    [audioOutputSelect, setAudioOutputMediaDevice],
  );

  // initialize selections from localStorage only once on mount
  const [initialized, setInitialized] = useState(false);
  const enumerationTimeoutRef = useRef(null);

  useEffect(() => {
    if (initialized || !room?.localParticipant) return;

    if (enumerationTimeoutRef.current) {
      clearTimeout(enumerationTimeoutRef.current);
      enumerationTimeoutRef.current = null;
    }

    // check if device enumeration has completed (at least one device found)
    const hasAnyDevices = audioInputDevices.length > 0 || videoInputDevices.length > 0 || audioOutputDevices.length > 0;

    if (hasAnyDevices) {
      // devices found - enumeration complete, apply preferences immediately
      if (audioInputMediaDevice && audioInputDevices.length > 0 && audioInputDevices.some((d) => d.deviceId === audioInputMediaDevice)) {
        handleAudioInputChange(audioInputMediaDevice);
      }
      if (videoInputMediaDevice && videoInputDevices.length > 0 && videoInputDevices.some((d) => d.deviceId === videoInputMediaDevice)) {
        handleVideoInputChange(videoInputMediaDevice);
      }
      if (
        audioOutputMediaDevice &&
        audioOutputDevices.length > 0 &&
        audioOutputDevices.some((d) => d.deviceId === audioOutputMediaDevice)
      ) {
        handleAudioOutputChange(audioOutputMediaDevice);
      }
      setInitialized(true);
    } else {
      // no devices yet - wait a bit for enumeration to complete
      // if still empty after timeout, assume enumeration complete (no devices available)
      enumerationTimeoutRef.current = setTimeout(() => setInitialized(true), 500);
    }

    return () => {
      if (enumerationTimeoutRef.current) {
        clearTimeout(enumerationTimeoutRef.current);
        enumerationTimeoutRef.current = null;
      }
    };
  }, [
    initialized,
    room,
    audioInputMediaDevice,
    videoInputMediaDevice,
    audioOutputMediaDevice,
    audioInputDevices,
    videoInputDevices,
    audioOutputDevices,
    handleAudioInputChange,
    handleVideoInputChange,
    handleAudioOutputChange,
  ]);

  // The icon travels with the section rather than being matched on the title downstream —
  // the menu is a dumb renderer and shouldn't have to know what "Speaker" means.
  return {
    microphone: {
      title: 'Microphone',
      icon: Mic,
      devices: audioInputDevices,
      activeDeviceId: audioInputSelect.activeDeviceId,
      onChange: handleAudioInputChange,
    },
    camera: {
      title: 'Video',
      icon: Video,
      devices: videoInputDevices,
      activeDeviceId: videoInputSelect.activeDeviceId,
      onChange: handleVideoInputChange,
    },
    speaker: {
      title: 'Speaker',
      icon: Volume2,
      devices: audioOutputDevices,
      activeDeviceId: audioOutputSelect.activeDeviceId,
      onChange: handleAudioOutputChange,
    },
  };
}
